import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { intelligenceAPI } from '../lib/api';
import { toast } from 'sonner';
import { ScanSearch, Briefcase, ShieldAlert, ChevronRight } from 'lucide-react';

const fitColor = (fit) => {
  if (!fit) return { c: '#64748B', b: 'rgba(100,116,139,0.12)' };
  const f = fit.toLowerCase();
  if (f.includes('strong')) return { c: '#059669', b: 'rgba(16,185,129,0.12)' };
  if (f.includes('good')) return { c: '#0076A3', b: 'rgba(30,104,255,0.1)' };
  if (f.includes('partial') || f.includes('moderate')) return { c: '#D97706', b: 'rgba(217,119,6,0.12)' };
  return { c: '#DC2626', b: 'rgba(220,38,38,0.1)' };
};

const fraudColor = (level) => {
  const l = (level || '').toLowerCase();
  if (l === 'high' || l === 'critical') return '#DC2626';
  if (l === 'medium') return '#D97706';
  return '#059669';
};

export default function ResumeIntelligencePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await intelligenceAPI.listResumes();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load resume intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }} data-testid="resume-intel-loading">Analyzing resumes...</div>;
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }} data-testid="resume-intelligence-page">
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#002A38', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <ScanSearch size={26} color="#00A0E0" /> Resume Intelligence
        </h1>
        <p style={{ color: '#64748B', fontSize: '0.9375rem', marginTop: '0.25rem' }} data-testid="resume-intel-count">
          AI analysis of {items.length} parsed resume{items.length === 1 ? '' : 's'} across all your jobs
        </p>
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 1rem', background: '#FAF7F2', borderRadius: 16, border: '1px dashed #CBD5E1' }} data-testid="resume-intel-empty">
          <ScanSearch size={40} color="#94A3B8" style={{ margin: '0 auto 1rem' }} />
          <div style={{ fontWeight: 700, color: '#334155', fontSize: '1.0625rem' }}>No analyzed resumes yet</div>
          <p style={{ color: '#64748B', fontSize: '0.875rem', marginTop: '0.5rem', maxWidth: 440, margin: '0.5rem auto 1.25rem' }}>
            Upload resumes inside a job — CHIRE parses each one, scores the fit, and surfaces skills and integrity signals here.
          </p>
          <button onClick={() => navigate('/jobs')} data-testid="resume-intel-go-to-jobs" style={{ padding: '0.625rem 1.25rem', borderRadius: 999, background: '#00A0E0', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
            Go to Jobs
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {items.map((c) => {
            const fc = fitColor(c.fit_recommendation);
            return (
              <div
                key={c._id}
                data-testid={`resume-row-${c._id}`}
                onClick={() => navigate(`/candidates/${c._id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.125rem 1.25rem', background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00A0E0'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,165,233,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ECE7DE'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#00A0E0,#0076A3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                  {(c.name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#002A38', fontSize: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#64748B', fontSize: '0.8125rem', marginTop: '0.1875rem' }}>
                    <Briefcase size={13} /> {c.job_title || '—'}
                  </div>
                  {c.skills && c.skills.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {c.skills.slice(0, 6).map((s, idx) => (
                        <span key={`${s}-${idx}`} style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem', borderRadius: 999, background: '#F1F5F9', color: '#475569' }}>{s}</span>
                      ))}
                      {c.skills.length > 6 && <span style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>+{c.skills.length - 6}</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }} title="Overall match score">
                    <div style={{ fontWeight: 800, fontSize: '1.125rem', color: '#002A38' }}>{c.overall_score != null ? `${c.overall_score}%` : '—'}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>Match</div>
                  </div>
                  {c.fraud_score != null && (
                    <div style={{ textAlign: 'center' }} title={`Integrity risk: ${c.fraud_level || 'low'}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 700, fontSize: '0.9375rem', color: fraudColor(c.fraud_level) }}>
                        <ShieldAlert size={14} />{c.fraud_score}
                      </div>
                      <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>Risk</div>
                    </div>
                  )}
                  <span style={{ padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: fc.b, color: fc.c }}>
                    {c.fit_recommendation || 'Unknown'}
                  </span>
                  <ChevronRight size={18} color="#CBD5E1" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
